import { useEffect, useState } from 'react';
import styled from "styled-components";
import { colors } from 'src/common/colors';
import {Table, Thead, Tbody, Tr, Th, Td, TableContainer, Checkbox, Box, useDisclosure, } from '@chakra-ui/react'
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { SearchBar } from '../common/SearchBar';
import ActionButton from '../common/ActionButton';
import SPagination from '../common/SPagination'; 
import { formatStartDate, formatDateToDot } from '../../common/util'
import useChangeClubState from 'src/hooks/ManageClubJoin/useChangeClubState';
import SToast from '../common/SToast';
import SModal from '../common/SModal';
import useDeleteClub from 'src/hooks/ManageClubJoin/useDeleteClub';
import useGetClubsInactive from 'src/hooks/SearchInfo/useGetClubsInactive';
import useGetClubsActive from 'src/hooks/SearchInfo/useGetClubsActive';

export default function ClubJoinList() {
    const { active } = useParams()

    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState<number>(parseInt(searchParams.get('page') || '1'));
    
    const [searchKeyword, setSearchKeyword] = useState('');
    const { getClubsActive, activeClubSearchResults } = useGetClubsActive();
    const { getClubsInactive, inactiveClubSearchResults } = useGetClubsInactive();
    const { changeClubState } = useChangeClubState();
    const { deleteClub } = useDeleteClub();
    const { errorToast, successToast, customErrorToast } = SToast();
    const [checkedClubIds, setCheckedClubIds] = useState<number[]>([]);
    const [allChecked, setAllChecked] = useState(false);
    let clubSearchResults = active === 'inactive' ? inactiveClubSearchResults : activeClubSearchResults;
    let activeState = active === 'inactive' ? '비활성' : '활성';
    let activeChangeState = active === 'inactive' ? '활성' : '비활성';

    const { 
        isOpen: isOpenDeleteModal, 
        onOpen: onOpenDeleteModal, 
        onClose: onCloseDeleteModal
    } = useDisclosure();

    const { 
        isOpen: isOpenUpdateModal, 
        onOpen: onOpenUpdateModal, 
        onClose: onCloseUpdateModal
    } = useDisclosure();

    const updateSearchParams = (newPage: number, newKeyword: string) => {
        const params: { page?: string; keyword?: string } = {};
        if (newPage !== 1) params.page = newPage.toString();
        if (newKeyword) params.keyword = newKeyword;
        setSearchParams(params);
    };

    const handlePageChange = (selectedPage: number) => {
        setPage(selectedPage);
        setCheckedClubIds([]);
        setAllChecked(false);
        updateSearchParams(selectedPage, searchKeyword);
    };

    const getClubList = async () => {
        if (active === 'activePaid') {
            getClubsActive(searchKeyword, page, 'true')
        } else if (active === 'activeUnpaid') {
            getClubsActive(searchKeyword, page, 'false')
        } else {
            getClubsInactive(searchKeyword, page);
        }
    }

    const handleAllCheckboxChange = (e: { target: { checked: any; }; }) => {
        const isChecked = e.target.checked;
        setAllChecked(isChecked);
      
        if (isChecked) {
          const allItemIds = clubSearchResults?.content.map((club) => club.academyId) || [];
          setCheckedClubIds(allItemIds);
        } else {
          setCheckedClubIds([]);
        }
    };

    const handleCheckboxChange = (itemId: number) => {       
        const updatedCheckedItemIds = [...checkedClubIds];
    
        if (updatedCheckedItemIds.includes(itemId)) {
          const index = updatedCheckedItemIds.indexOf(itemId);
          if (index !== -1) {
            updatedCheckedItemIds.splice(index, 1);
          }
        } else {
            updatedCheckedItemIds.push(itemId);
        }
      
        const allItemIds = clubSearchResults?.content.map((club) => club.academyId) || [];
        setAllChecked(updatedCheckedItemIds.length === allItemIds.length);
      
        setCheckedClubIds(updatedCheckedItemIds);
    };

    const onSearch = async () => {
        setPage(1);
        updateSearchParams(1, searchKeyword);
        await getClubList();
    };

    const onChangeClubState = async () => {
        if (checkedClubIds.length === 0) {
            customErrorToast('상태를 변경할 클럽을 선택해주세요.')
        } else {
            let changeCount = 0;

            for (const clubId of checkedClubIds) {
                const changeCall = await changeClubState(clubId);
                if (changeCall === 200) {
                    changeCount++;
                } else {
                    errorToast();
                }
            }

            if (changeCount > 0) {
                successToast(`${changeCount}개 항목이 성공적으로 변경 됐습니다.`);
                await getClubList();
                setCheckedClubIds([]);
                setAllChecked(false);
            }
        }
    };

    const onDeleteClub = async () => {
        if (checkedClubIds.length === 0) {
            customErrorToast('삭제할 클럽을 선택해주세요.')
        } else {
            let changeCount = 0;

            for (const clubId of checkedClubIds) {
                const changeCall = await deleteClub(clubId);
                if (changeCall === 200) {
                    changeCount++;
                } else {
                    errorToast();
                }
            }

            if (changeCount > 0) {
                successToast(`${changeCount}개 항목이 성공적으로 삭제 됐습니다.`);
                await getClubList();
                setCheckedClubIds([]);
                setAllChecked(false);
            }
        }
    };

    useEffect(() => {
        const pageFromUrl = parseInt(searchParams.get('page') || '1');
        setPage(pageFromUrl);
        getClubList();
    }, [active, searchParams]);
    
    return(
        <>
        <Container>
            <Title>클럽 가입 신청 관리</Title>
            <SubContainer>
                <SubTitle>{activeState} 클럽 목록</SubTitle>
                {active === 'active'
                ? <ActionButton 
                    backgroundColor={colors.button.red} 
                    _hover={{bg: "hover.red", color:'white'}} 
                    text='선택 항목 비활성' 
                    onClick={onOpenUpdateModal}
                />
                : <>
                    <ActionButton 
                        backgroundColor={colors.button.blue} 
                        _hover={{bg: "hover.blue", color:'white'}} 
                        text='선택 항목 활성' 
                        onClick={onOpenUpdateModal}
                    />
                    <ActionButton 
                        backgroundColor={colors.button.red} 
                        _hover={{bg: "hover.red", color:'white'}} 
                        text='선택 항목 삭제' 
                        onClick={onOpenDeleteModal}
                    />
                </>}

                <SModal 
                    title={`${activeChangeState} 하기`}
                    desc={`정말로 ${activeChangeState} 하겠습니까?`}
                    onClick={onChangeClubState} 
                    isOpen={isOpenUpdateModal} 
                    onOpen={onOpenUpdateModal} 
                    onClose={onCloseUpdateModal} 
                    color={activeChangeState === '활성' ? 'blue' : 'red'}
                />
                <SModal 
                    title={`삭제하기`}
                    desc={`정말로 삭제하겠습니까?`}
                    onClick={onDeleteClub} 
                    isOpen={isOpenDeleteModal} 
                    onOpen={onOpenDeleteModal} 
                    onClose={onCloseDeleteModal} 
                    color={'red'}
                />
            </SubContainer>
            <TableContainer height='100%'>
            <Box borderRadius="8px" overflow="hidden" borderLeft='1px' borderRight='1px' borderColor='brand.grey'>
                <Table variant='simple' size='md'>
                    <Thead backgroundColor='brand.grey'>
                        <Tr>
                            <Th textAlign="center" width='7%' height='61px' borderRight='1px' borderBottom='none' borderColor='brand.labelGrey' verticalAlign='middle'>
                                <Checkbox
                                    isChecked={allChecked}
                                    onChange={handleAllCheckboxChange}
                                    borderColor='brand.navy'
                                />
                            </Th>
                            <Th width='7%' textAlign="center" verticalAlign='middle' borderRight='1px' borderColor='brand.labelGrey' borderBottom='none' fontSize='18px' fontFamily='KFAGothic'>순서</Th>
                            <Th width='26%' textAlign="center" verticalAlign='middle' borderRight='1px' borderColor='brand.labelGrey' borderBottom='none'>
                                <LabelText>클럽명</LabelText>  
                            </Th>
                            <Th width='34%' textAlign="center" verticalAlign='middle' borderRight='1px' borderColor='brand.labelGrey' borderBottom='none'>
                                <LabelText>클럽주소</LabelText>  
                            </Th>
                            <Th width='13%' textAlign="center" verticalAlign='middle' borderRight='1px' borderColor='brand.labelGrey' borderBottom='none'>
                                <LabelText>상태</LabelText>  
                            </Th>
                            <Th width='13%' textAlign="center" verticalAlign='middle' fontSize='18px' fontFamily='KFAGothic'>
                                신청일
                            </Th>
                        </Tr>
                    </Thead>
                    <Tbody fontSize='16px'>
                    {clubSearchResults?.content.map((a,i)=>{
                        const totalItems = clubSearchResults?.totalElements || 0;
                        const orderNumber = totalItems - ((page - 1) * 8 + i);
                        return (
                        <Tr key={a.academyId}>
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderRight='1px' borderColor='brand.grey' height='72px'>
                                <Checkbox
                                    isChecked={checkedClubIds.includes(a.academyId)}
                                    onChange={() => { handleCheckboxChange(a.academyId); }}
                                    borderColor='brand.navy'
                                />
                            </Td>
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderRight='1px' borderColor='brand.grey'>{orderNumber}</Td>
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderRight='1px' borderColor='brand.grey' textDecoration='underline' textUnderlineOffset='4px'>
                                <Link to={`/manage-club-join/${active}/${a.academyId}`}>{a.academyName}</Link>
                            </Td> 
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderRight='1px' borderColor='brand.grey'>
                                {a.academyAddress}
                            </Td>
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderRight='1px' borderColor='brand.grey'>
                                {a.active === true ? '활성' : '비활성'}
                            </Td>
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderColor='brand.grey'>
                                {formatDateToDot(formatStartDate(parseInt(a.appliedDate)))}
                            </Td>
                        </Tr>)
                    })}
                    </Tbody>
                </Table>
            </Box>
            </TableContainer>
            <BottomContainer>
                <SearchBar 
                    placeholder='클럽명 검색' 
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onEnterPress={(e) => {
                        if (e.key === "Enter") {
                          onSearch();
                        }
                    }}
                />
                <SPagination 
                    activePage={page} 
                    totalItemsCount={clubSearchResults?.totalElements}
                    handlePageChange={handlePageChange}
                />
            </BottomContainer>
        </Container>
        </>
    )
}

const Container = styled.div`
width: calc(100vw - 506px);
min-width: 850px;
`

const Title = styled.div`
color: ${colors.black};
font-family: 'KFAGothicBold';
font-size: 22px;
margin-bottom: 41px;
`

const SubContainer = styled.div`
display: flex;
button {
    margin-left: 12px;
    margin-bottom: 15px;
}
`

const SubTitle = styled.span`
color: ${colors.black};
font-size: 18px;
line-height: 40px;
margin-right: auto;
`

const BottomContainer = styled.div`
display: flex;
justify-content: space-between;
margin-top: 43px;
margin-bottom: 51px;
`

const LabelText = styled.span`
font-size: 18px;
font-family: 'KFAGothic';
`;
 